/**
 * `plan-sharepoint-ingest` — CLM-43. The eighth endpoint.
 *
 * An admin pastes a **SharePoint folder link**. The server enumerates it, applies
 * the `Signed` + PDF filter, and returns a **plan**: what it found, what it would
 * ingest, what it would skip and why. Nothing is ingested by this call.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## Why a dry-run plan rather than "start the backfill"
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Enumerating is cheap; extracting is not. The backfill is ~500 SOWs, which at
 * ~10 contracts/month is **roughly four years of volume in one go** — and several
 * ratified decisions rest on that figure (CLAUDE.md rule 17, architecture §0.1:
 * Batch API dropped, brute-force vectors sufficient, Opus 5 with no cost
 * trade-off). A call that started 500 extractions from one pasted URL would make
 * that decision on the admin's behalf. Rule 7 says surface it.
 *
 * The plan is also where the two data realities become visible *before* several
 * hundred extractions have run:
 *
 *  - **`party_key` may not be derivable at all.** 85.7% of Qfly clients carry no
 *    `sow_initials`, and CLM-12's B-24 rule is *error and exclude* — no fallback
 *    to `account_name`, none to a Dynamics field, nothing invented. So this is
 *    reported **per document, as a skip reason**, and lifted into `totals` as a
 *    headline count. It is the single most likely reason a backfill stalls.
 *  - **`sow_number` is not one pattern.** 15+ shapes including `#`, a bare `7660`
 *    and a free-text project name. A candidate matching two known SOW numbers is
 *    reported `sow_number_ambiguous` for a human, never resolved by picking the
 *    longest.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## POST, and no scope input
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `POST` for the three reasons the registry gives for `search`: the call writes an
 * audit row so it is not a cacheable idempotent `GET`; and a sharing link in a
 * query string would put the tenant's folder structure into proxy and CDN access
 * logs, which is a needless second copy of where cloudEQ's contracts live.
 *
 * As everywhere in this package there is **no scope, party or role input**.
 * Authorisation is resolved server-side from Entra group claims, and the schema is
 * `.strict()` so an attempt to send one is a validation error rather than a no-op.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## No `provenanced()` here, on purpose
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `provenanced()` wraps values the extraction pipeline can populate, so that a
 * fact cannot be represented without the evidence that makes it evidence (CR-01).
 * A plan asserts **no facts about a contract**. `sow_number` on a planned document
 * is a *candidate match against a folder name*, not a value CLM has recorded, and
 * `party_key` is a resolution the admin is being asked to approve. Wrapping either
 * one would claim provenance for something nothing has yet decided.
 *
 * When the approved plan is executed, the resulting contract's `sow_number` and
 * `party_key` carry `origin: 'intake_metadata'` — which is exactly what makes
 * FR-29 deterministic (§4A.1) — and *that* is where provenance attaches.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## Type pending (CLM-122, D-110-4 point 4)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * A document whose name and folders give no type and no usable SOW number, or
 * whose name contradicts itself, is a **planned** row with a
 * `type_pending_reason`, not a skip: `OTHER` as a placeholder, no SOW number,
 * filed `contract_type_confirmation 'pending'` for the AI to type after filing
 * (CLM-118's pipeline step). A client never pre-ticks one, and no Select all
 * reaches a `no_name_type` row: the reviewer ticks each and confirms only its
 * client. No model is called here. `totals` count every skip and Type pending
 * reason, and the extraction calls the Type pending rows would cost.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## Documents are referenced, never copied
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Every document in this response is identified by `drive_id` + `item_id` + `etag`
 * and a `web_url` the front end follows. There is no byte count-adjacent field
 * that could hold content, no download URL, and no hash of content — because
 * nothing in this path reads content. Signed documents stay in SharePoint (IR-05);
 * the facts about them live in exactly one place and are referenced everywhere
 * else.
 */
import { z } from 'zod';
import { HttpsUrlSchema, IsoTimestampUtcSchema, PartyKeySchema, ContractKeySchema } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
import { AmendmentSignalNameSchema, ContractTypeNameEvidenceSchema, } from '../vocabulary.js';
import { SelectablePartySchema } from './upload.js';
/* -------------------------------------------------------------------------- */
/* Identifiers                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * A Graph `driveItem.id` or `drive.id`.
 *
 * Not `OpaqueIdSchema`: that caps at 64 characters and Graph ids routinely exceed
 * it. Opaque all the same — clients round-trip it and must not parse it.
 */
export const GraphItemIdSchema = z.string().min(1).max(512);
/**
 * A Graph `eTag`, **verbatim** — typically `"{GUID},N"`, quotes included.
 *
 * Carried unmodified because it is an opaque comparison token: the value stored by
 * one run is compared for equality by the next, and tidying the quotes away would
 * make every re-run look like a change.
 */
export const ETagSchema = z.string().min(1).max(200);
/** A folder name from SharePoint. People type these, so almost nothing is ruled out. */
export const FolderSegmentSchema = z.string().min(1).max(400);
/* -------------------------------------------------------------------------- */
/* Vocabularies                                                                */
/* -------------------------------------------------------------------------- */
/** Why the `Signed` + PDF filter did not select a file. See `@clm/sharepoint`. */
export const INGEST_EXCLUSION_REASONS = [
    'no_signed_marker',
    /**
     * `signed` was present but glued to a preceding letter, so it is part of another
     * word — `UNSIGNED`, `Countersigned`, `Redesigned`. Reported with the word, so
     * `Countersigned` is a line an admin can act on rather than a document that
     * silently never arrived.
     */
    'signed_marker_inside_another_word',
    'not_pdf',
    'unusable_file_name',
];
export const IngestExclusionReasonSchema = z.enum(INGEST_EXCLUSION_REASONS);
/** Why a matched document is not in the ingest set. */
export const INGEST_SKIP_REASONS = [
    /** Data reality 1 — B-24 errors and excludes rather than inventing a key. */
    'party_key_not_derivable',
    /** The folder name yields nothing usable as a lookup key — `#`, free text. */
    'sow_number_unusable',
    'sow_number_not_matched',
    /** Data reality 2 — two or more known SOW numbers match. A human decides. */
    'sow_number_ambiguous',
    /** Idempotency: same `(drive_id, item_id, etag)` as a previous run. */
    'already_ingested',
    /** Graph returned no `eTag`, so the item cannot be keyed for idempotency. */
    'no_etag',
    /**
     * CLM-88 (D-88-2). The SOW-number prefix in the file name and the folder name
     * resolve to two different parties. A human decides — correctable. Appended at
     * the END: enum order is API surface.
     */
    'party_ambiguous',
];
export const IngestSkipReasonSchema = z.enum(INGEST_SKIP_REASONS);
/**
 * CLM-122 (D-110-4 point 4) — why a planned row is **Type pending**: filed as
 * `OTHER`, `contract_type_confirmation 'pending'`, for the AI to type after
 * filing. Each value is the `type_case` CLM-118's pipeline step records for the
 * row (the `satisfies` pins it):
 *
 *  - `no_name_type` — the file and folder names give no type word and no
 *    SOW-shaped number, and no number resolved (evidence `untyped`);
 *  - `conflicting_names` — the name contradicts itself (evidence `conflicting`),
 *    so it is never filed with the planner's own type (Q-118-2).
 *
 * Mirrored in `@clm/sharepoint` `plan.ts`; the api test pins the two equal.
 */
export const INGEST_TYPE_PENDING_REASONS = [
    'no_name_type',
    'conflicting_names',
];
export const IngestTypePendingReasonSchema = z.enum(INGEST_TYPE_PENDING_REASONS);
/**
 * CLM-122 (R-122-6) — what the planner listed a document under: a skip reason,
 * or a Type pending reason of a planned row, as a reviewer's skip records it
 * (CLM-115). The skip reasons first, then the two, so neither order moves.
 */
export const INGEST_PLANNER_REASONS = [...INGEST_SKIP_REASONS, ...INGEST_TYPE_PENDING_REASONS];
export const IngestPlannerReasonSchema = z.enum(INGEST_PLANNER_REASONS);
/**
 * Confidence in the `sow_number` candidate a folder name yielded.
 *
 * `dominant_shape` is the IR-01 shape (`CGISOW26201OV`). Everything else is
 * weaker, and the weak values are surfaced rather than hidden: a match made from a
 * `plausible` candidate is a match a human may want to look at.
 */
export const SOW_CANDIDATE_SHAPES = [
    'dominant_shape',
    /**
     * Carries the `SOW` infix but not the dominant shape — `MCDSOW9003OV`,
     * `24SSOW9001OV`, `MCDSOW24007CRa`, `BAISOW25387OVCR-004`. Every one a real
     * spike value the dominant shape rejects.
     */
    'sow_infix',
    'plausible',
    'free_text_shape',
    'too_long',
    'punctuation_only',
    /**
     * Letters but no digits — `Adtalem`, `September`, the bare word `SOW`. Every
     * `sow_number` shape the spike recorded carries at least one digit, including
     * those with no infix (`7660`, `ASK-001`), so a digitless token is a word from
     * the filename rather than a candidate.
     */
    'no_digits',
    'absent',
];
export const SowCandidateShapeSchema = z.enum(SOW_CANDIDATE_SHAPES);
/**
 * Which of the **two live conventions** produced the match.
 *
 * `file_name` — **SharePoint**, the backfill source. The folder is the *client*
 * and the number is a token in the filename:
 * `Adtalem-Covista/Adtalem - AGESOW25264OV - September 2025 - Signed.pdf`.
 *
 * `folder_name` — **Teams**, per `docs/integration-decisions.md` §2, where the
 * number is the folder-name *prefix*: `SAISOW25486OV-Sainsburys - …`.
 *
 * Both systems exist and neither is going away, so both are tried and this field
 * records which answered. Surfaced rather than hidden because a plan mixing the two
 * is worth seeing: it means the crawl spanned systems.
 */
export const SOW_CANDIDATE_SOURCES = ['file_name', 'folder_name'];
export const SowCandidateSourceSchema = z.enum(SOW_CANDIDATE_SOURCES);
/**
 * What the pipeline will do with a document once intake has landed it.
 *
 * `manual_entry` is CLAUDE.md rule 4 / §4C: the contract is stored, converted
 * in-tenant, indexed and searchable, and **never sent to an LLM**. Its facts are
 * entered by hand. Only the reasoning step is withheld — which is why a restricted
 * document appears in `to_ingest` with this disposition and *not* in `to_skip`. A
 * skip would quietly lose a client's contracts.
 */
export const INGEST_DISPOSITIONS = ['extract', 'manual_entry'];
export const IngestDispositionSchema = z.enum(INGEST_DISPOSITIONS);
/**
 * How the planner arrived at a `to_ingest` row's SOW number — a plan *state*, never
 * a skip reason (D-63-1): both states are executed.
 *
 * `sow_number_registered` — the candidate matched a contract CLM already holds.
 * `sow_number_unregistered` — G26 (a): a `dominant_shape` candidate for a *resolved*
 * party matched no stored contract for any party in the crawl. Accepted, reported
 * with a `caveat`, and reconciled later against the source system (FR-15, CLM-35).
 */
export const PLAN_STATES = [
    'sow_number_registered',
    'sow_number_unregistered',
    'sow_number_manual',
    /** CLM-76 (D-76-9, appended at END). An MSA / NDA / OTHER row: no SOW number applies (D-71-0 is SOW-only). */
    'sow_number_not_applicable',
];
export const PlanStateSchema = z.enum(PLAN_STATES);
/**
 * CLM-76 (D-76-1). What an ingest path may file. Never `AMENDMENT` — it needs
 * `amends_contract_key` (0005:503), which no bulk path can supply. `OTHER` is
 * never read from a name (D-76-2): a bulk row is `OTHER` only as the Type pending
 * placeholder (CLM-122). Since CLM-118 nobody picks a type at plan review (B9),
 * so no type list is offered at all.
 */
export const INGEST_CONTRACT_TYPES = ['MSA', 'NDA', 'SOW', 'OTHER'];
export const IngestContractTypeSchema = z.enum(INGEST_CONTRACT_TYPES);
/**
 * CLM-76 (D-76-3). `exact` — a type token in the name or folder; `inferred` — no
 * token, a SOW number resolved. CLM-118 (R-118-6) appends `conflicting`: the
 * name names more than one type, or a type word beside a SOW-shaped number;
 * since CLM-122 such a row is Type pending (`conflicting_names`), never filed
 * with the planner's own type. CLM-122 appends `untyped`: no type in the name —
 * Type pending (`no_name_type`). Enum order is API surface.
 */
export const TYPE_CONFIDENCES = ['exact', 'inferred', 'conflicting', 'untyped'];
export const TypeConfidenceSchema = z.enum(TYPE_CONFIDENCES);
/**
 * CLM-76. `derived` by the planner, or `manual`. Since CLM-118 (R-118-18) there is
 * no reviewer type override, so a receipt is always `derived`; `manual` stays in
 * the enum only so an older receipt still parses.
 */
export const CONTRACT_TYPE_SOURCES = ['derived', 'manual'];
export const ContractTypeSourceSchema = z.enum(CONTRACT_TYPE_SOURCES);
/**
 * CLM-71 (D-71-0). The skip reasons an admin may correct at plan review with
 * "Correct and include" — a SOW number the planner could not derive, or a party it
 * could not resolve. `already_ingested` and `no_etag` are not correctable: the
 * first is idempotency, the second cannot be approved at all (an approved item
 * requires an eTag). Folder-level party ambiguity (one segment, two parties) is
 * folded into `party_key_not_derivable` by the planner; `party_ambiguous` (CLM-88)
 * is the *prefix vs folder* disagreement and is correctable — the admin picks.
 */
export const CORRECTABLE_SKIP_REASONS = [
    'party_key_not_derivable',
    'sow_number_unusable',
    'sow_number_not_matched',
    'sow_number_ambiguous',
    'party_ambiguous',
];
/**
 * CLM-71 (D-71-3). Whether the ingested row's party came from the planner
 * (`derived`, folder name → CRM party) or from the admin's correction (`manual`).
 * Sits beside `plan_state`, which stays a single enum: `sow_number_manual` iff the
 * number was typed, so a row may show both a typed number and a manual party.
 * CLM-88 (D-88-4) appends `sow_prefix` — the party came from the SOW-number
 * prefix in the file name (a scoped lookup, never an invention). Appended at the END.
 */
export const PARTY_KEY_SOURCES = ['derived', 'manual', 'sow_prefix'];
export const PartyKeySourceSchema = z.enum(PARTY_KEY_SOURCES);
/** CLM-88. The sources the planner itself emits on a planned row — never `manual`, which is a receipt value. */
export const PLANNED_PARTY_KEY_SOURCES = ['derived', 'sow_prefix'];
export const PlannedPartyKeySourceSchema = z.enum(PLANNED_PARTY_KEY_SOURCES);
/**
 * The caveat carried by every `sow_number_manual` row and by every row whose party
 * was set by hand. Owned here, mirrored verbatim in `@clm/sharepoint` `plan.ts`,
 * pinned equal by the api test exactly as {@link CAVEAT_UNREGISTERED} is. CLM-71.
 */
export const CAVEAT_MANUAL = 'SOW number and/or party entered by hand at plan review; validated against shape, scope and stored contracts. ' +
    'Reconciliation (FR-15) checks it like any other.';
/**
 * The caveat carried by every `sow_number_unregistered` row — one policy string,
 * owned here so the web app reads the same wording the planner writes. The planner
 * (`@clm/sharepoint`, which takes no dependency on this package) carries a verbatim
 * mirror; `apps/api/test/sharepoint-ingest-plan.test.ts` pins the two equal so a
 * wording change in one place fails a test rather than drifting silently.
 */
export const CAVEAT_UNREGISTERED = 'SOW number recognised in the file name (dominant shape) but CLM holds no contract with it ' +
    '(checked against the parties in this crawl only). ' +
    'Accepted per G26 (a); a wrong number is caught by corpus reconciliation (FR-15) as extra / party_mismatch.';
/**
 * The caveat carried by every `to_ingest` row planned under `include_unsigned_pdfs`
 * whose filename carries no `signed` marker (`signed_marker: false`). Owned here,
 * mirrored verbatim in `@clm/sharepoint` `plan.ts`, pinned equal by the api test
 * exactly as {@link CAVEAT_UNREGISTERED} is. An unregistered unsigned row carries
 * both, space-joined — under the 600-char cap on `caveat` (D-88-6). CLM-67.
 */
export const CAVEAT_UNSIGNED = "Filename carries no 'signed' marker — approve only if you know this is the executed document.";
/**
 * CLM-88 (D-88-1). The caveat on every row whose party came from the SOW-number
 * prefix in the file name rather than a folder (`party_key_source: 'sow_prefix'`).
 * Owned here, mirrored verbatim in `@clm/sharepoint` `plan.ts`, pinned equal by the
 * api test exactly as {@link CAVEAT_UNREGISTERED} is.
 */
export const CAVEAT_PARTY_FROM_PREFIX = 'Client derived from the SOW-number prefix in the file name (first three letters = party_key); no folder named it.';
/**
 * CLM-88 (D-88-3). The unregistered caveat for a **weak-shape** number
 * (`sow_number_shape: 'sow_infix'`) accepted because its prefix named the party. A
 * separate constant from {@link CAVEAT_UNREGISTERED}, whose first sentence claims
 * the dominant shape — the web tooltip repeats a caveat's first sentence verbatim.
 * Owned here, mirrored verbatim in `@clm/sharepoint` `plan.ts`, pinned equal by the
 * api test.
 */
export const CAVEAT_UNREGISTERED_WEAK = 'SOW number recognised in the file name (weak shape: letters + SOW + digits, not the dominant 3+SOW+5+2 shape) but CLM holds no contract with it ' +
    '(checked against the parties in this crawl only). Stored as written, upper-cased, no 0/O repair (D-88-3). ' +
    'Accepted per G26 (a); a wrong number is caught by corpus reconciliation (FR-15) as extra / party_mismatch.';
/* -------------------------------------------------------------------------- */
/* Request                                                                     */
/* -------------------------------------------------------------------------- */
/** Applied when `max_documents` is omitted. Server-side, not a Zod `.default()`. */
export const DEFAULT_PLAN_MAX_DOCUMENTS = 500;
export const MAX_PLAN_MAX_DOCUMENTS = 2000;
export const PlanSharepointIngestRequestSchema = z
    .object({
    /**
     * The pasted sharing link, exactly as SharePoint's "Copy link" produced it.
     *
     * It is **not** directly addressable in Graph — the modern forms carry an
     * opaque token rather than coordinates — so the server resolves it via
     * `GET /shares/{base64url-encoded-url}/driveItem`. The client sends the URL and
     * nothing else; it does not encode, parse or split it.
     */
    folder_link: HttpsUrlSchema,
    /**
     * Cap on documents in this plan. Omit for {@link DEFAULT_PLAN_MAX_DOCUMENTS}.
     *
     * The CLM-32 accuracy pilot passes **20**: a deliberate first run, which is
     * why reaching the cap sets `truncated` rather than being an error. The crawl
     * is breadth-first, so a truncated plan draws from many client folders rather
     * than exhausting the first two alphabetically.
     */
    max_documents: z.number().int().min(1).max(MAX_PLAN_MAX_DOCUMENTS).optional(),
    /**
     * Default false. When true, PDFs the filter classifies `no_signed_marker` are
     * planned as candidates with `signed_marker: false` and are never pre-approved
     * by the client — see `filter.ts` for why the default is the safe one. CLM-67.
     */
    include_unsigned_pdfs: z.boolean().optional(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Response                                                                    */
/* -------------------------------------------------------------------------- */
export const ScannedRootSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    name: z.string().min(1).max(400),
    /**
     * `null` where Graph omitted it. Nullable rather than defaulted to an empty
     * string: `''` would reach the front end as a link that goes nowhere, whereas
     * `null` is a link the UI knows not to render.
     */
    web_url: HttpsUrlSchema.nullable(),
    /** False when the pasted link addressed a single file rather than a folder. */
    is_folder: z.boolean(),
});
/**
 * A document the plan would ingest.
 *
 * `drive_id` + `item_id` is the durable reference (an item id is unique only
 * within a drive); `etag` is its version. Together they are the idempotency key —
 * see `already_ingested` and `ingest_state`.
 */
export const PlannedDocumentSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    etag: ETagSchema,
    file_name: z.string().min(1).max(400),
    web_url: HttpsUrlSchema.nullable(),
    size_bytes: z.number().int().min(0).nullable(),
    last_modified_utc: IsoTimestampUtcSchema.nullable(),
    /** Enclosing folder names below the pasted link, outermost first. */
    folder_path: z.array(FolderSegmentSchema),
    /** Resolved server-side from the folder path or the SOW-number prefix. Never sent by a client. */
    party_key: PartyKeySchema,
    /**
     * CLM-88 (D-88-4). `sow_prefix` when the party came from the SOW-number prefix in
     * the file name (D-88-1); `derived` when a folder named or confirmed it. The
     * planner never emits `manual` here — that is the execute receipt's value.
     */
    party_key_source: PlannedPartyKeySourceSchema,
    /** IR-01 — reused from the source system, never minted. CLM-76: `null` iff `contract_type !== 'SOW'`. */
    sow_number: z.string().min(1).max(32).nullable(),
    sow_number_shape: SowCandidateShapeSchema,
    /** True when the candidate equalled the SOW number rather than merely prefixing it. */
    sow_number_exact: z.boolean(),
    sow_number_source: SowCandidateSourceSchema,
    /**
     * CLM-76 (D-76-1). Derived from a name / folder token, or inferred from a
     * resolved SOW number; `OTHER` only as the Type pending placeholder (CLM-122).
     */
    contract_type: IngestContractTypeSchema,
    /** CLM-76 (D-76-3). The confidence flag the review surface tags; `conflicting` and `untyped` are Type pending. */
    contract_type_confidence: TypeConfidenceSchema,
    /**
     * CLM-118 (R-118-3) — what the deterministic file-name rule found: `typed`, or
     * `conflicting` (R-118-6). A row typed only by a reviewer's number is `untyped`
     * (`applyManualCorrection` step 2b), and so (CLM-122) is a `no_name_type` Type
     * pending row. Written on the contract at execute.
     */
    contract_type_name_evidence: ContractTypeNameEvidenceSchema,
    /**
     * CLM-122 (D-110-4 point 4) — set iff the row is **Type pending**: filed as
     * `OTHER`, `contract_type_confirmation 'pending'`, no SOW number, for the AI to
     * type after filing. A client never pre-ticks one; no Select all reaches a
     * `no_name_type` row (R-122-4). `null` otherwise.
     */
    type_pending_reason: IngestTypePendingReasonSchema.nullable(),
    /** CLM-118 (R-118-14) — the amendment word in the name or folder, if any. Written on the contract at execute. */
    amendment_signal_name: AmendmentSignalNameSchema.nullable(),
    /**
     * The filename or folder segment the SOW number was recognised in — which of the
     * two is stated by `sow_number_source`.
     */
    matched_from: z.string().min(1).max(400),
    /** An existing contract for this SOW, where CLM already has one. */
    existing_contract_key: ContractKeySchema.nullable(),
    /** G26 (a). `sow_number_unregistered` rows always have `existing_contract_key: null`. */
    plan_state: PlanStateSchema,
    /**
     * Human-readable caveat: `CAVEAT_UNREGISTERED` (or `CAVEAT_UNREGISTERED_WEAK`,
     * CLM-88) for an unregistered number, `CAVEAT_PARTY_FROM_PREFIX` for a
     * prefix-derived party, `CAVEAT_UNSIGNED`, or any of them space-joined in that
     * order; `null` when registered, folder-derived and signed. Cap 600 (D-88-6).
     */
    caveat: z.string().min(1).max(600).nullable(),
    /**
     * `true` when the filename carried the marker; `false` only under
     * `include_unsigned_pdfs`. Clients must not pre-select a `false` row. CLM-67.
     */
    signed_marker: z.boolean(),
    /** §4C, inherited from the party. */
    processing_restricted: z.boolean(),
    disposition: IngestDispositionSchema,
    /**
     * `changed` when the ledger holds this item under a **different** eTag — the
     * document was re-signed or replaced since the last run, so it is picked up
     * again rather than skipped.
     */
    ingest_state: z.enum(['new', 'changed']),
    previous_etag: ETagSchema.nullable(),
}).superRefine((value, ctx) => {
    // CLM-76 (D-76-6): a SOW carries its number; MSA / NDA / OTHER never do (D-71-0, ck_contract_sow_number_type).
    if (value.contract_type === 'SOW' && value.sow_number === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sow_number'], message: 'a SOW row carries its SOW number (IR-01)' });
    }
    if (value.contract_type !== 'SOW' && value.sow_number !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sow_number'], message: 'only a SOW row carries a SOW number (D-71-0)' });
    }
    /* CLM-122 — a Type pending row is exactly the OTHER placeholder, with the evidence its reason implies. */
    const pending = value.type_pending_reason;
    if ((pending !== null) !== (value.contract_type === 'OTHER')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type_pending_reason'],
            message: 'a bulk row is OTHER exactly when it is Type pending (D-76-2: OTHER is never read from a name)',
        });
    }
    const untypedPending = value.contract_type === 'OTHER' && value.contract_type_name_evidence === 'untyped';
    if ((pending === 'no_name_type') !== (value.contract_type_confidence === 'untyped') || (pending === 'no_name_type') !== untypedPending) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type_pending_reason'],
            message: 'no_name_type is exactly an OTHER row with confidence and evidence untyped',
        });
    }
    if ((pending === 'conflicting_names') !== (value.contract_type_confidence === 'conflicting')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type_pending_reason'],
            message: 'conflicting_names is exactly a row with confidence conflicting (R-122-3)',
        });
    }
    if (pending === 'conflicting_names' && value.contract_type_name_evidence !== 'conflicting') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['contract_type_name_evidence'],
            message: 'a conflicting_names row carries evidence conflicting',
        });
    }
    if (pending !== null && value.plan_state !== 'sow_number_not_applicable') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['plan_state'],
            message: 'a Type pending row carries no SOW number, so its plan_state is sow_number_not_applicable',
        });
    }
});
export const SkippedDocumentSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    /** `null` for the `no_etag` skip, which is the one case there is none. */
    etag: ETagSchema.nullable(),
    file_name: z.string().min(1).max(400),
    web_url: HttpsUrlSchema.nullable(),
    folder_path: z.array(FolderSegmentSchema),
    reason: IngestSkipReasonSchema,
    /**
     * Operator-facing explanation. Says which rule excluded the document and what
     * would change the answer, because "skipped: party_key_not_derivable" on 300
     * lines is a wall, not a report.
     */
    detail: z.string().min(1).max(1000),
    /**
     * The competing SOW numbers, for `sow_number_ambiguous`; the two party keys
     * (prefix first, folder second) for `party_ambiguous` (CLM-88). Empty otherwise.
     */
    candidates: z.array(z.string().min(1).max(32)),
    /**
     * CLM-126 — up to 3 nearest-match candidates, trigram-scored, for
     * `party_key_not_derivable` and `party_ambiguous` rows (R-126-16). Fetched
     * for every scanned document up front (`planIngest` cannot know in advance
     * which will be unresolved); empty here when either this row resolved
     * cleanly or nothing cleared `PARTY_MATCH_FLOOR`. A **suggestion only** — an
     * Admin accepts or rejects it through "Correct and include", never applied
     * silently (FR-14 extended).
     */
    nearest_matches: z.array(SelectablePartySchema).max(3),
    /**
     * CLM-71. `true` when `reason` is one of {@link CORRECTABLE_SKIP_REASONS}: the
     * admin may supply the SOW number and/or party at plan review and include the
     * row. `false` for `already_ingested` and `no_etag`.
     */
    correctable: z.boolean(),
    /**
     * CLM-76. The type the planner derived from a name / folder token before the
     * row was skipped; `null` when no token was found. Nobody chooses a type
     * (CLM-118); read with {@link contract_type_name_evidence}, it says what a
     * correction would file.
     */
    derived_type: IngestContractTypeSchema.nullable(),
    /**
     * CLM-122 (R-122-7) — what the file-name rule found for this row (folder and
     * root numbers included). A party skip whose evidence is `untyped` is filed as
     * Type pending once its client is corrected (no number needed); one whose
     * evidence is `conflicting` is filed as Type pending and takes no number.
     */
    contract_type_name_evidence: ContractTypeNameEvidenceSchema,
});
/**
 * CLM-115. A document a reviewer decided not to ingest, remembered across
 * plans.
 *
 * It is returned rather than merely omitted because the browser keeps no plan
 * between crawls (D-67-6): a fresh tab pressing "Plan ingest" can only render a
 * Skipped section from what the server hands it.
 *
 * These rows leave `to_skip` entirely and are counted separately. `to_skip`
 * keeps meaning what it has always meant — the planner's verdicts about the
 * world — and a human's decision is a different kind of fact, so it does not
 * become a new {@link INGEST_SKIP_REASONS} member. The planner's original
 * reason rides along as `planner_reason` so the Skipped list can still say why
 * the document was in front of somebody in the first place.
 */
export const UserSkippedDocumentSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    file_name: z.string().min(1).max(400),
    web_url: HttpsUrlSchema.nullable(),
    folder_path: z.array(FolderSegmentSchema),
    /**
     * What the planner had listed it under when the reviewer skipped it: a skip
     * reason, or (CLM-122, R-122-6) the Type pending reason of a planned row.
     */
    planner_reason: IngestPlannerReasonSchema,
    skipped_by_upn: z.string().min(3).max(320),
    skipped_utc: z.string().datetime({ offset: true }),
    /**
     * The version the reviewer looked at; `null` where the planner had none (the
     * `no_etag` case). Kept so {@link changed_since_skip} can be computed.
     */
    skipped_etag: ETagSchema.nullable(),
    /**
     * D-115-1. `true` when the document has been re-signed or replaced since the
     * decision was taken — the crawled eTag no longer matches `skipped_etag`.
     *
     * The row stays hidden from `to_skip` regardless, which is the decision: a
     * reviewer who has judged a document does not want it back in the queue
     * because somebody re-uploaded it. This flag is how it stays *visible*
     * without being *queued*, so a document skipped because it was wrong, then
     * fixed, cannot go missing in silence.
     */
    changed_since_skip: z.boolean(),
});
/** A file the `Signed` + PDF filter did not select. */
export const ExcludedFileSchema = z.object({
    drive_id: GraphItemIdSchema,
    item_id: GraphItemIdSchema,
    file_name: z.string().min(1).max(400),
    folder_path: z.array(FolderSegmentSchema),
    reason: IngestExclusionReasonSchema,
    /** The word `signed` was found inside, lower-cased. `null` for other reasons. */
    matched_word: z.string().min(1).max(80).nullable(),
});
const CountSchema = z.number().int().min(0);
/** CLM-122 (R-122-10) — `to_skip` per reason: every {@link INGEST_SKIP_REASONS} member, zero-filled. */
export const IngestSkipReasonCountsSchema = z
    .object(Object.fromEntries(INGEST_SKIP_REASONS.map((reason) => [reason, CountSchema])))
    .strict();
/** CLM-122 (R-122-10) — the Type pending rows per {@link INGEST_TYPE_PENDING_REASONS} member. */
export const IngestTypePendingCountsSchema = z
    .object({
    no_name_type: CountSchema,
    conflicting_names: CountSchema,
})
    .strict();
export const IngestPlanTotalsSchema = z.object({
    folders_visited: z.number().int().min(0),
    files_seen: z.number().int().min(0),
    documents_matched: z.number().int().min(0),
    to_ingest: z.number().int().min(0),
    to_skip: z.number().int().min(0),
    /**
     * CLM-115 — documents held back by a reviewer's earlier decision. These are
     * not in `to_skip`: they left the planner's verdict buckets when somebody
     * judged them, and `documents_matched` still counts them, so
     * `to_ingest + to_skip + user_skipped` is what the crawl actually matched.
     */
    user_skipped: z.number().int().min(0),
    /** CLM-76 (D-76-3) — `to_ingest` rows whose type was inferred from a SOW number, not a token. */
    contract_type_inferred: z.number().int().min(0),
    excluded_files: z.number().int().min(0),
    /** Rule 4 — how many of `to_ingest` will never reach an LLM. */
    manual_entry: z.number().int().min(0),
    /** Data reality 1, lifted out of the skip list because it is the headline. */
    party_key_not_derivable: z.number().int().min(0),
    /** Data reality 2 — unusable + unmatched + ambiguous, i.e. needs a human. */
    sow_number_needs_resolution: z.number().int().min(0),
    /**
     * G26 (a) — `to_ingest` rows whose dominant-shape number CLM does not yet hold.
     * Not a failure: they will be ingested, and reconciliation (FR-15) checks them.
     */
    sow_number_unregistered: z.number().int().min(0),
    /**
     * CLM-67 — `to_ingest` rows with `signed_marker: false`. Always 0 unless the plan
     * was made with `include_unsigned_pdfs: true`; each such row needs a human tick.
     */
    unsigned_candidates: z.number().int().min(0),
    /** CLM-88 (D-88-5) — `to_ingest` rows whose party came from the SOW-number prefix. */
    party_from_sow_prefix: z.number().int().min(0),
    /** CLM-126 — `to_skip` rows (`party_key_not_derivable` / `party_ambiguous`) that carry at least one nearest-match candidate. */
    nearest_match_offered: z.number().int().min(0),
    /** Idempotency made visible: how much of a re-run was a no-op. */
    already_ingested: z.number().int().min(0),
    changed_since_ingest: z.number().int().min(0),
    /**
     * Rule 17. True when this one run would ingest more than a month of design
     * volume. An advisory on a screen an admin is about to approve, never a refusal
     * — but if it is true and stays true, architecture §0.1 needs re-opening.
     * CLM-122 (R-122-12): `to_ingest` counts Type pending rows, ticked or not.
     */
    exceeds_design_volume: z.boolean(),
    /** CLM-122 — `to_ingest` rows that are Type pending (`type_pending_reason` set). */
    type_pending: CountSchema,
    /** CLM-122 (R-122-10) — the Type pending rows per reason; sums to `type_pending`. */
    type_pending_by_reason: IngestTypePendingCountsSchema,
    /** CLM-122 (R-122-10) — `to_skip` per reason, every reason present; sums to `to_skip`. */
    to_skip_by_reason: IngestSkipReasonCountsSchema,
    /**
     * CLM-122 (R-122-11) — the extraction calls the Type pending rows would cost if
     * every one were ticked: one `extractContract` per row whose disposition is
     * `extract`, none for a restricted client (rule 4); the type rides on that
     * reading. A count, not money — nothing holds a per-call price. Clause
     * adjudication, model-assisted segmentation, embeddings and conversion, which
     * any filed contract may get, are not counted.
     */
    type_pending_extraction_calls: CountSchema,
}).superRefine((value, ctx) => {
    /* CLM-122 (R-122-10) — the counts by reason check their own arithmetic. */
    const sum = (counts) => Object.values(counts).reduce((total, count) => total + count, 0);
    if (sum(value.type_pending_by_reason) !== value.type_pending) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['type_pending_by_reason'], message: 'the Type pending counts by reason sum to type_pending' });
    }
    if (sum(value.to_skip_by_reason) !== value.to_skip) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to_skip_by_reason'], message: 'the skip counts by reason sum to to_skip' });
    }
    if (value.type_pending > value.to_ingest) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['type_pending'], message: 'Type pending rows are to_ingest rows' });
    }
    if (value.type_pending_extraction_calls > value.type_pending) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type_pending_extraction_calls'],
            message: 'at most one extraction call per Type pending row',
        });
    }
});
export const PlanSharepointIngestResponseSchema = z.object({
    root: ScannedRootSchema,
    to_ingest: z.array(PlannedDocumentSchema),
    to_skip: z.array(SkippedDocumentSchema),
    /**
     * CLM-115. Documents a reviewer has already judged, newest decision first.
     * Held back from `to_skip` rather than dropped from the response, because the
     * client has no memory of its own to build a Skipped section from.
     */
    user_skipped: z.array(UserSkippedDocumentSchema),
    excluded_files: z.array(ExcludedFileSchema),
    totals: IngestPlanTotalsSchema,
    /** True when the crawl stopped at `max_documents` with folders left to visit. */
    truncated: z.boolean(),
    /**
     * The plan changes nothing, and says so in the type.
     *
     * Structurally `true`. This endpoint has no "and now do it" mode: executing an
     * approved plan is a separate, explicitly-authorised call, so a client cannot
     * accidentally trigger 500 extractions by setting a flag on a read.
     */
    dry_run: z.literal(true),
    /**
     * Present even though nothing was mutated. NFR-07 requires **all access** to be
     * logged, not all change, and this call enumerated the folder structure of
     * cloudEQ's client contracts — which is precisely the kind of access an
     * evidence chain should carry.
     */
    audit: AuditReceiptSchema,
});
/**
 * Superseded by CLM-50 / CLM-67: the ledger is `0061`, the endpoint is
 * `execute-sharepoint-ingest` (approval by `(drive_id, item_id, etag)` triple, no
 * `plan_id`), and the planner surface ticks and executes. Kept as history.
 *
 * ⚑ **Flagged for Gerry, not decided here.**
 *
 * This endpoint plans; nothing executes the plan. Executing it needs three things
 * this story deliberately does not build:
 *
 *  1. **A ledger table.** Idempotency is keyed on `(drive_id, item_id, etag)` and
 *     there is nowhere to store it — `clm.contract` has `sharepoint_item_url` but
 *     no `drive_id`, `item_id` or `etag`, and `intake_path` is CHECK-constrained to
 *     `('Connect', 'Manual')` with no value for a backfill. See the CLM-43 report
 *     for the proposed migration in range `0060`–`0079`.
 *  2. **A `plan_id`.** An admin approving *this* plan and an executor acting on
 *     *that* one must be the same plan, or the approval means nothing. That needs
 *     the plan persisted, which needs (1).
 *  3. **A ninth endpoint** — `execute-sharepoint-ingest` — taking a `plan_id` and
 *     a separate `contract:bulk_ingest` confirmation, and enqueuing to
 *     `clm-conversion`. Not folded into this one on purpose: rule 2 says intake is
 *     where this story stops, and a `confirm: true` flag on a planning call is how
 *     a dry run becomes a live 500-document run by typo.
 */
//# sourceMappingURL=plan-sharepoint-ingest.js.map